import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface ImpersonatedUser {
  id: string;
  user_id: string;
  display_name: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  avatar_url: string | null;
}

interface ImpersonationSession {
  sessionId: string;
  targetUserId: string;
  startedAt: string;
}

interface ImpersonationContextType {
  isImpersonating: boolean;
  impersonatedUser: ImpersonatedUser | null;
  impersonationSession: ImpersonationSession | null;
  startImpersonation: (targetUserId: string) => Promise<boolean>;
  endImpersonation: () => Promise<void>;
  effectiveUserId: string | null;
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

const IMPERSONATION_STORAGE_KEY = 'admin_impersonation_session';
const MAX_IMPERSONATION_HOURS = 4;

export function ImpersonationProvider({ children }: { children: React.ReactNode }) {
  const { user: adminUser } = useAuth();
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedUser, setImpersonatedUser] = useState<ImpersonatedUser | null>(null);
  const [impersonationSession, setImpersonationSession] = useState<ImpersonationSession | null>(null);

  // Check for existing impersonation session on mount
  useEffect(() => {
    const checkExistingSession = async () => {
      const stored = localStorage.getItem(IMPERSONATION_STORAGE_KEY);
      if (!stored) return;

      try {
        const session: ImpersonationSession = JSON.parse(stored);
        
        // Check if session has expired (4 hours)
        const startedAt = new Date(session.startedAt);
        const hoursElapsed = (Date.now() - startedAt.getTime()) / (1000 * 60 * 60);
        
        if (hoursElapsed >= MAX_IMPERSONATION_HOURS) {
          await endImpersonation();
          return;
        }

        // Verify admin still has admin role
        if (!adminUser) return;

        const { data: roleData } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', adminUser.id)
          .eq('role', 'admin')
          .single();

        if (!roleData) {
          await endImpersonation();
          return;
        }

        // Load impersonated user data
        const { data: userData } = await supabase
          .from('profiles')
          .select('id, user_id, display_name, first_name, last_name, avatar_url')
          .eq('user_id', session.targetUserId)
          .single();

        if (userData) {
          setImpersonatedUser({
            ...userData,
            email: null // We don't have direct access to auth.users
          });
          setImpersonationSession(session);
          setIsImpersonating(true);
        } else {
          await endImpersonation();
        }
      } catch {
        localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
      }
    };

    if (adminUser) {
      checkExistingSession();
    }
  }, [adminUser]);

  const startImpersonation = useCallback(async (targetUserId: string): Promise<boolean> => {
    if (!adminUser) return false;

    try {
      // Verify current user is admin
      const { data: roleData } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', adminUser.id)
        .eq('role', 'admin')
        .single();

      if (!roleData) {
        console.error('User is not an admin');
        return false;
      }

      // Check if target is an admin (cannot impersonate admins)
      const { data: targetRoleData } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', targetUserId)
        .eq('role', 'admin');

      if (targetRoleData && targetRoleData.length > 0) {
        console.error('Cannot impersonate admin users');
        return false;
      }

      // Create impersonation session record
      const { data: sessionData, error: sessionError } = await supabase
        .from('user_impersonation_sessions')
        .insert({
          admin_user_id: adminUser.id,
          target_user_id: targetUserId,
          ip_address: null // Would need server-side to get real IP
        })
        .select('id')
        .single();

      if (sessionError) throw sessionError;

      // Log the action
      await supabase.rpc('log_admin_action', {
        p_admin_user_id: adminUser.id,
        p_target_user_id: targetUserId,
        p_action: 'impersonate',
        p_details: JSON.parse(JSON.stringify({ session_id: sessionData.id })),
        p_ip_address: null,
        p_user_agent: navigator.userAgent
      });

      // Load target user data
      const { data: userData, error: userError } = await supabase
        .from('profiles')
        .select('id, user_id, display_name, first_name, last_name, avatar_url')
        .eq('user_id', targetUserId)
        .single();

      if (userError) throw userError;

      const session: ImpersonationSession = {
        sessionId: sessionData.id,
        targetUserId,
        startedAt: new Date().toISOString()
      };

      // Store session in localStorage
      localStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(session));

      setImpersonatedUser({
        ...userData,
        email: null
      });
      setImpersonationSession(session);
      setIsImpersonating(true);

      return true;
    } catch (error) {
      console.error('Failed to start impersonation:', error);
      return false;
    }
  }, [adminUser]);

  const endImpersonation = useCallback(async () => {
    if (impersonationSession) {
      try {
        // Update session end time
        await supabase
          .from('user_impersonation_sessions')
          .update({ ended_at: new Date().toISOString() })
          .eq('id', impersonationSession.sessionId);
      } catch (error) {
        console.error('Failed to update impersonation session:', error);
      }
    }

    // Clear state
    localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    setIsImpersonating(false);
    setImpersonatedUser(null);
    setImpersonationSession(null);
  }, [impersonationSession]);

  // The effective user ID - if impersonating, use the impersonated user's ID
  const effectiveUserId = isImpersonating && impersonatedUser 
    ? impersonatedUser.user_id 
    : adminUser?.id ?? null;

  return (
    <ImpersonationContext.Provider
      value={{
        isImpersonating,
        impersonatedUser,
        impersonationSession,
        startImpersonation,
        endImpersonation,
        effectiveUserId
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
}

export function useImpersonation() {
  const context = useContext(ImpersonationContext);
  if (context === undefined) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider');
  }
  return context;
}
